import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { HeadingNode, QuoteNode } from '@lexical/rich-text'
import { generateBlockId } from '@shared/ast/idGenerator'
import { ParagraphNode, RootNode } from 'lexical'
import { useEffect } from 'react'

import { $createCustomHeadingNode } from '../nodes/CustomHeadingNode'
import { $createCustomParagraphNode } from '../nodes/CustomParagraphNode'
import { $createCustomQuoteNode } from '../nodes/CustomQuoteNode'

export function NodeReplacementPlugin(): null {
  const [editor] = useLexicalComposerContext()

  useEffect(() => {
    const removeParagraphTransform = editor.registerNodeTransform(ParagraphNode, (node) => {
      if (node.getType() === 'custom-paragraph') {
        return
      }

      const customNode = $createCustomParagraphNode(generateBlockId())
      customNode.setFormat(node.getFormatType())
      customNode.setDirection(node.getDirection())
      customNode.setIndent(node.getIndent())

      const children = node.getChildren()
      for (const child of children) {
        customNode.append(child)
      }

      node.replace(customNode)
    })

    const removeHeadingTransform = editor.registerNodeTransform(HeadingNode, (node) => {
      if (node.getType() === 'custom-heading') {
        return
      }

      const tag = node.getTag()
      const level = parseInt(tag.replace('h', ''), 10) as 1 | 2 | 3 | 4 | 5 | 6

      const customNode = $createCustomHeadingNode(generateBlockId(), level)
      customNode.setFormat(node.getFormatType())
      customNode.setDirection(node.getDirection())
      customNode.setIndent(node.getIndent())

      const children = node.getChildren()
      for (const child of children) {
        customNode.append(child)
      }

      node.replace(customNode)
    })

    const removeQuoteTransform = editor.registerNodeTransform(QuoteNode, (node) => {
      if (node.getType() === 'custom-quote') {
        return
      }

      const customNode = $createCustomQuoteNode(generateBlockId())
      customNode.setFormat(node.getFormatType())
      customNode.setDirection(node.getDirection())
      customNode.setIndent(node.getIndent())

      const children = node.getChildren()
      for (const child of children) {
        customNode.append(child)
      }

      node.replace(customNode)
    })

    // Deleting a select-all selection can empty the root outright, leaving a
    // document with no blocks at all. Everything downstream then misbehaves: the
    // caret has no block to sit in, list commands no-op, and saving that state
    // produces a file that crashes the editor on the next load ("the editor state
    // is empty"). Put the line back and park the caret in it.
    const removeEmptyRootTransform = editor.registerNodeTransform(RootNode, (root) => {
      if (root.getChildrenSize() > 0) {
        return
      }
      const paragraph = $createCustomParagraphNode(generateBlockId())
      root.append(paragraph)
      paragraph.select()
    })

    return () => {
      removeParagraphTransform()
      removeHeadingTransform()
      removeQuoteTransform()
      removeEmptyRootTransform()
    }
  }, [editor])

  return null
}
